/**
 * Semaphore Utility
 *
 * Implements a semaphore pattern to control concurrent access to resources.
 * Ensures that only a limited number of operations can run simultaneously.
 *
 * CRITICAL USE CASE: Cart synchronization operations
 * - Prevents race conditions when multiple sync calls occur
 * - Ensures sequential execution (permits = 1)
 * - Proper queue management with FIFO ordering
 */
export class Semaphore {
    private permits: number;

    private maxPermits: number;

    private queue: Array<() => void> = [];

    /**
     * Create a new Semaphore
     * @param maxPermits Maximum number of concurrent operations allowed (default: 1 for mutex behavior)
     */
    constructor(maxPermits: number = 1) {
        this.maxPermits = maxPermits;
        this.permits = maxPermits;
    }

    /**
     * Acquire a permit
     * Blocks (returns a promise) until a permit is available
     *
     * @returns Promise that resolves when permit is acquired
     */
    async acquire(): Promise<void> {
        if (this.permits > 0) {
            this.permits--;
            return Promise.resolve();
        }

        // No permits available, queue the request
        return new Promise<void>(resolve => {
            this.queue.push(resolve);
        });
    }

    /**
     * Release a permit
     * Allows the next queued operation to proceed
     */
    release(): void {
        this.permits++;

        // Process next queued operation if any
        if (this.queue.length > 0 && this.permits > 0) {
            this.permits--;
            const resolve = this.queue.shift();
            if (resolve) {
                resolve();
            }
        }
    }

    /**
     * Execute a function with semaphore protection
     * Automatically acquires permit before execution and releases after
     *
     * @param fn Function to execute
     * @returns Promise with the function's return value
     */
    async execute<T>(fn: () => Promise<T>): Promise<T> {
        await this.acquire();
        try {
            return await fn();
        } finally {
            this.release();
        }
    }

    /**
     * Get current number of available permits
     */
    getAvailablePermits(): number {
        return this.permits;
    }

    /**
     * Get number of queued operations
     */
    getQueueLength(): number {
        return this.queue.length;
    }

    /**
     * Check if semaphore is locked (no permits available)
     */
    isLocked(): boolean {
        return this.permits === 0;
    }

    /**
     * Reset semaphore to initial state
     * WARNING: This will clear the queue without executing pending operations
     */
    reset(): void {
        this.permits = this.maxPermits;
        this.queue = [];
    }
}
